/**
 * Putting a module into a classroom.
 *
 * A module is not built here. Sharing one means pointing the room at a playlist the
 * account has already published: nothing is copied and nothing is re-published, so
 * the room shows the same post — which is why a module in a classroom can be played,
 * commented on and reacted to like anything else, exactly as a capsule and a book can
 * (see `AddCapsuleDialog` and `AddFlashbookDialog`, which this dialog is the third
 * of).
 *
 * What is different about a module is *what a teacher would do here*, and it changes
 * the shape of the sheet:
 *
 *  - **The unit they set is often already built.** A teacher with a year of capsules
 *    has gathered them into modules on their own profile, and re-gathering them inside
 *    the room would be busywork — so the sheet is a library of the account's own
 *    published modules, and choosing one is one press.
 *  - **The unit they have not built yet is built *in* the room.** The other half of
 *    the flow is a module assembled from the room's own capsules, and that is the
 *    module builder — opened attached to this classroom, so publishing it lands here
 *    and comes back here. It is the primary action when the library is empty, because
 *    that is the teacher a course is waiting for.
 *
 * Two things this sheet never offers, and both for the same reason as the other two
 * dialogs: **a draft** (a module with no post is the creator's own unfinished work,
 * and a room cannot show what has not been published) and **another room's module**
 * (a module marked as belonging to one classroom is that room's, and offering it here
 * would put it in two).
 */
import {useCallback, useMemo, useState} from 'react'
import {ScrollView, StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {AppBskyEmbedVideo} from '@atproto/api'
import {Plural, Trans, useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {gradeLabel, subjectLabel} from '#/lib/curriculum'
import {type NavigationProp} from '#/lib/routes/types'
import {niceDate} from '#/lib/strings/time'
import {
  canPostInClassroom,
  type Classroom,
  libraryEntryVisibility,
  useClassroomActor,
} from '#/state/classrooms'
import {
  useClassroomPostViews,
  usePostViewsByUri,
} from '#/state/queries/classroom-posts'
import {
  useAddClassroomContentMutation,
  useClassroomContentQuery,
} from '#/state/queries/classrooms'
import {
  type ActorModuleRecord,
  useActorModulesQuery,
} from '#/state/queries/module'
import {useSession} from '#/state/session'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {Check_Stroke2_Corner0_Rounded as Check} from '#/components/icons/Check'
import {Loader} from '#/components/Loader'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {showClassroomError} from './showClassroomError'

export function AddModuleDialog({
  control,
  classroom,
}: {
  control: DialogControlProps
  classroom: Classroom
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const actor = useClassroomActor()
  const navigation = useNavigation<NavigationProp>()
  const {hasSession} = useSession()
  const {gtMobile} = useBreakpoints()

  const canPost = canPostInClassroom(classroom, actor)
  const [selectedUri, setSelectedUri] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  const {data: inRoomPages} = useClassroomContentQuery(classroom.id, 'module')
  const entries = inRoomPages?.pages.flatMap(page => page.content) ?? []
  const addContent = useAddClassroomContentMutation(classroom.id)
  /** Which of the account's modules the room already holds, by any address. */
  const alreadyHere = useMemo(() => {
    const uris = new Set<string>()
    for (const entry of entries) {
      if (entry.moduleUri) uris.add(entry.moduleUri)
      /**
       * The post as well as the record.
       *
       * A module added from here is recorded by its record's address, but a build
       * that only knew the post would have stored that instead — and a room that
       * offered the same playlist twice, under two addresses it cannot tell apart,
       * is the bug the dedupe in `addContent` exists to prevent.
       */
      if (entry.postUri) uris.add(entry.postUri)
    }
    return uris
  }, [entries])

  const {data: records, isPending} = useActorModulesQuery(actor?.did)
  const library = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return (
      (records ?? [])
        .filter(record => record.status !== 'draft' && !!record.postUri)
        .filter(record => record.visibility !== 'classroom')
        /* A room pinned to a year holds that year's work and no other (see the capsule picker). */
        .filter(record => !classroom.grade || record.level === classroom.grade)
        .filter(record =>
          needle
            ? `${record.title} ${record.description}`
                .toLowerCase()
                .includes(needle)
            : true,
        )
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    )
  }, [classroom.grade, query, records])

  /*
   * The covers, batched — the same route the other two pickers take.
   *
   * A module's picture is not on its own record; it is the first capsule's still,
   * which lives on that capsule's post, and the card here draws the same three
   * thumbnails the grid card does. One request for the whole sheet.
   */
  const postUris = useMemo(
    () =>
      library.flatMap(record =>
        record.items
          .slice(0, 3)
          .map(item => item.postUri)
          .filter((uri): uri is string => !!uri),
      ),
    [library],
  )
  const {data: postViews} = useClassroomPostViews(postUris)
  const viewsByUri = usePostViewsByUri(postViews)
  const thumbnailsFor = useCallback(
    (record: ActorModuleRecord): (string | undefined)[] =>
      record.items.slice(0, 3).map(item => {
        if (!item.postUri) return undefined
        const embed = viewsByUri.get(item.postUri)?.embed
        return embed && AppBskyEmbedVideo.isView(embed)
          ? embed.thumbnail
          : undefined
      }),
    [viewsByUri],
  )

  const selected = useMemo(
    () => library.find(record => record.uri === selectedUri) ?? null,
    [library, selectedUri],
  )

  const onAdd = useCallback(() => {
    if (!actor || !selected) return
    addContent.mutate(
      {
        classroomId: classroom.id,
        kind: 'module',
        // Published work is public in the room unless it is classroom-only.
        visibility: libraryEntryVisibility(selected),
        text: selected.description,
        moduleTitle: selected.title,
        moduleUri: selected.uri,
        moduleCid: selected.cid,
        postUri: selected.postUri,
        postCid: selected.postCid,
      },
      {
        onSuccess: () => Toast.show(l`Module added to ${classroom.name}`),
        onError: error => showClassroomError(i18n, error),
      },
    )
    setSelectedUri(null)
    control.close()
  }, [
    actor,
    addContent,
    classroom.id,
    classroom.name,
    control,
    i18n,
    l,
    selected,
  ])

  const onOpenStudio = useCallback(() => {
    setSelectedUri(null)
    control.close()
    /*
     * The room travels with the builder, so publishing there puts the module in
     * this classroom and returns the teacher to the tab they left.
     */
    navigation.navigate('ModuleStudio', {classroomId: classroom.id})
  }, [classroom.id, control, navigation])

  if (!hasSession || !canPost) {
    return (
      <Dialog.Outer control={control}>
        <Dialog.Handle />
        <Dialog.ScrollableInner label={l`Add a module to this classroom`}>
          <View style={[a.gap_lg]}>
            <Text style={[a.text_xl, a.font_bold]}>
              {l`Adding is for whoever runs this classroom and its teachers`}
            </Text>
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              {l`The account that runs this classroom, and the teachers in it, put the work here. Students and parents play it.`}
            </Text>
          </View>
          <Dialog.Close />
        </Dialog.ScrollableInner>
      </Dialog.Outer>
    )
  }

  const loading = isPending && (records ?? []).length === 0
  const nothingAtAll = !isPending && library.length === 0

  return (
    <Dialog.Outer control={control} onClose={() => setSelectedUri(null)}>
      <Dialog.Handle />
      <Dialog.ScrollableInner
        testID="classroomModulePicker"
        label={l`Add a module to this classroom`}
        style={[a.w_full, {maxWidth: 640}]}>
        <View style={[a.gap_md, a.pt_md]}>
          <View style={[a.gap_xs]}>
            <Text style={[a.text_xl, a.font_bold]}>
              <Trans>Add a module</Trans>
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              <Trans>
                A module is a playlist of capsules: one subject, one level,
                played in order as one run. Pick one you have published, or
                build a new one out of this classroom's capsules.
              </Trans>
            </Text>
          </View>

          {loading ? (
            <View style={[a.flex_row, a.align_center, a.gap_sm, a.py_2xl]}>
              <Loader size="sm" />
              <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
                <Trans>Looking for your modules…</Trans>
              </Text>
            </View>
          ) : nothingAtAll ? (
            /*
             * No module to share — which is not a dead end.
             *
             * A teacher whose capsules are already in the room has everything a
             * module needs and nothing assembled, so the primary action here is the
             * builder, which is where the six capsules are chosen.
             */
            <View
              testID="classroomModulePickerEmpty"
              style={[
                a.gap_md,
                a.p_lg,
                a.rounded_md,
                a.align_center,
                t.atoms.bg_contrast_25,
              ]}>
              <Text style={[a.text_md, a.font_semi_bold, a.text_center]}>
                <Trans>You have no modules to share yet.</Trans>
              </Text>
              <Text
                style={[
                  a.text_sm,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>
                  That is what the builder is for: gather six capsules of the
                  same subject and level, in the order they should be played,
                  and publish the module straight into this classroom.
                </Trans>
              </Text>
              <Button
                testID="classroomBuildModule"
                label={l`Build a module for this classroom`}
                size="large"
                color="primary"
                onPress={onOpenStudio}>
                <ButtonText>{l`Build a module`}</ButtonText>
              </Button>
            </View>
          ) : (
            <>
              <TextField.Root>
                <TextField.Input
                  testID="classroomModuleSearch"
                  label={l`Search your modules`}
                  placeholder={l`Search your modules`}
                  defaultValue={query}
                  onChangeText={setQuery}
                />
              </TextField.Root>

              {library.length === 0 ? (
                <View
                  testID="classroomModuleNoMatch"
                  style={[
                    a.gap_sm,
                    a.p_lg,
                    a.rounded_md,
                    a.align_center,
                    t.atoms.bg_contrast_25,
                  ]}>
                  <Text style={[a.text_md, a.font_semi_bold, a.text_center]}>
                    <Trans>No module matches that.</Trans>
                  </Text>
                  <Button
                    label={l`Clear the search`}
                    color="secondary"
                    size="small"
                    onPress={() => setQuery('')}>
                    <ButtonText>{l`Clear the search`}</ButtonText>
                  </Button>
                </View>
              ) : (
                <ScrollView
                  testID="classroomModuleGrid"
                  style={[{maxHeight: 360}]}
                  contentContainerStyle={[a.gap_sm]}>
                  {rows(library, gtMobile ? 3 : 2).map(row => (
                    <View
                      key={row.map(record => record.uri).join(' ')}
                      style={[a.flex_row, a.gap_sm, a.align_stretch]}>
                      {row.map(record => (
                        <View key={record.uri} style={[a.flex_1]}>
                          <ModuleChoiceCard
                            testID={`classroomModuleOption-${record.uri}`}
                            record={record}
                            selected={selectedUri === record.uri}
                            alreadyHere={alreadyHere.has(record.uri)}
                            thumbnails={thumbnailsFor(record)}
                            onPress={() =>
                              setSelectedUri(current =>
                                current === record.uri ? null : record.uri,
                              )
                            }
                          />
                        </View>
                      ))}
                      {Array.from({
                        length: (gtMobile ? 3 : 2) - row.length,
                      }).map((_, index) => (
                        <View key={`filler-${index}`} style={[a.flex_1]} />
                      ))}
                    </View>
                  ))}
                </ScrollView>
              )}

              <View style={[a.gap_sm, a.pt_xs]}>
                <Button
                  testID="classroomModuleSubmit"
                  label={l`Add this module to the classroom`}
                  size="large"
                  color="primary"
                  disabled={!selected}
                  onPress={onAdd}>
                  <ButtonText>{l`Add to this classroom`}</ButtonText>
                </Button>
                <Button
                  testID="classroomBuildModule"
                  label={l`Build a new module for this classroom`}
                  color="secondary"
                  size="small"
                  style={[a.self_start]}
                  onPress={onOpenStudio}>
                  <ButtonText>{l`Build a new module`}</ButtonText>
                </Button>
              </View>
            </>
          )}
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/** The library in rows, the way the other two pickers lay theirs out. */
function rows<T>(items: T[], columns: number): T[][] {
  const out: T[][] = []
  for (let index = 0; index < items.length; index += columns) {
    out.push(items.slice(index, index + columns))
  }
  return out
}

/**
 * One module, as a choice.
 *
 * The same card a profile's grid draws — three capsule thumbnails, then what a run
 * is worth — at the size a grid cell is, so a teacher recognises the module they are
 * choosing *as the thing they saw on their own page*. The two facts that decide
 * whether it can be chosen at all are printed on it: that it is already here, and
 * how long it is.
 */
function ModuleChoiceCard({
  testID,
  record,
  selected,
  alreadyHere,
  thumbnails,
  onPress,
}: {
  testID: string
  record: ActorModuleRecord
  selected: boolean
  alreadyHere: boolean
  thumbnails: (string | undefined)[]
  onPress: () => void
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()

  const level = gradeLabel(record.level)
  const subject = subjectLabel(record.subject)

  return (
    <Button
      testID={testID}
      label={l`${record.title}${record.itemCount ? `, ${record.itemCount} capsules` : ''}`}
      onPress={onPress}>
      {({hovered, pressed}) => (
        <View
          style={[
            styles.card,
            {
              borderColor: selected ? t.palette.primary_500 : 'transparent',
            },
            t.atoms.bg_contrast_25,
            hovered || pressed ? t.atoms.bg_contrast_50 : null,
          ]}>
          <View style={[styles.preview, a.flex_row, a.gap_xs, a.p_xs]}>
            {thumbnails.length > 0 ? (
              thumbnails.map((thumbnail, index) => (
                <View
                  key={`${record.uri}-${index}`}
                  style={[styles.slot, t.atoms.bg_contrast_50]}>
                  {thumbnail ? (
                    <Image
                      source={{uri: thumbnail}}
                      style={StyleSheet.absoluteFill}
                      contentFit="cover"
                      accessibilityIgnoresInvertColors
                    />
                  ) : null}
                </View>
              ))
            ) : (
              <>
                <View style={[styles.slot, t.atoms.bg_contrast_50]} />
                <View style={[styles.slot, t.atoms.bg_contrast_50]} />
                <View style={[styles.slot, t.atoms.bg_contrast_50]} />
              </>
            )}
          </View>

          <View style={[a.gap_xs, a.px_sm, a.pb_sm]}>
            <Text
              emoji
              numberOfLines={2}
              style={[a.text_sm, a.font_semi_bold, t.atoms.text]}>
              {record.title.trim() || l`Untitled module`}
            </Text>
            <View style={[a.flex_row, a.align_center, a.flex_wrap, a.gap_xs]}>
              <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                <Plural
                  value={record.itemCount}
                  one="# capsule"
                  other="# capsules"
                />
              </Text>
              {subject ? (
                <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                  · {l(subject)}
                </Text>
              ) : null}
              {level ? (
                <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                  · {l(level)}
                </Text>
              ) : null}
            </View>
            <Text style={[a.text_xs, t.atoms.text_contrast_low]}>
              {niceDate(i18n, record.createdAt, 'medium', 'none')}
            </Text>
            {alreadyHere ? (
              <View style={[styles.chip, t.atoms.bg_contrast_100]}>
                <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text]}>
                  <Trans>In this classroom</Trans>
                </Text>
              </View>
            ) : selected ? (
              <View style={[a.flex_row, a.align_center, a.gap_xs]}>
                <Check size="xs" fill={t.palette.primary_500} />
                <Text
                  style={[
                    a.text_xs,
                    a.font_semi_bold,
                    {color: t.palette.primary_500},
                  ]}>
                  <Trans>Chosen</Trans>
                </Text>
              </View>
            ) : null}
          </View>
        </View>
      )}
    </Button>
  )
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    gap: 6,
    padding: 6,
    borderRadius: 10,
    borderWidth: 2,
  },
  preview: {
    width: '100%',
    aspectRatio: 4 / 3,
  },
  slot: {
    flex: 1,
    borderRadius: 6,
    overflow: 'hidden',
  },
  chip: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
})
